import { useFormState } from "@/lib/Components/Form/Hooks/useFormState";
import {
  CreateRelocationInput,
  Currency,
  RelocationFieldsFragment,
  RelocationInclusionInput,
  RelocationInclusionType,
} from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import { FC } from "react";
import {
  DollarSignIcon,
  FuelIcon,
  SailboatIcon,
  ShipIcon,
  ShoppingCartIcon,
} from "lucide-react";
import {
  ExclamationTriangleIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { StarIcon } from "@heroicons/react/20/solid";
import { cn } from "@/lib/utils";
import { RelocationFuelInclusionInput } from "@/app/Relocations/Components/RelocationFuelInclusionInput";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { Fieldset, Legend } from "@/components/catalyst/fieldset";
import { useTranslations } from "use-intl";
import { Text } from "@/components/catalyst/text";
import { IconButton } from "@/lib/Components/Button/IconButton";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CurrencyInput } from "@/lib/Components/Form/Inputs/CurrencyInput";

export function RelocationInclusionForm({
  relocation,
  excludedTypes = [],
  currency,
}: {
  relocation?: RelocationFieldsFragment;
  excludedTypes?: RelocationInclusionType[];
  currency?: Currency;
}) {
  const { values, setFieldValue, isEditing } =
    useFormState<CreateRelocationInput>();
  const t = useTranslations("relocation");

  function deleteValue(value: RelocationInclusionInput) {
    //If we have an ID we need to set the inclusion to delete
    if (value.id) {
      setFieldValue("inclusions.delete", [
        ...(values.inclusions?.delete ?? []),
        value.id,
      ]);
    }

    setFieldValue(
      "inclusions.upsert",
      values.inclusions?.upsert?.filter((v) => {
        return v !== value;
      }),
    );
  }

  if (!isEditing) {
    return <RelocationsInclusionsViewCard relocation={relocation} />;
  }

  return (
    <div className="col-span-full space-y-12">
      {excludedTypes.includes(RelocationInclusionType.Fuel) ? null : (
        <RelocationFuelInclusionInput currency={currency} />
      )}
      {values.inclusions?.upsert?.map((inclusion, idx) => {
        if (
          inclusion.type === RelocationInclusionType.Fuel ||
          excludedTypes.includes(inclusion.type)
        ) {
          return null;
        }

        return (
          <Fieldset key={idx} className="relative">
            <Legend>{t(`inclusions.type.${inclusion.type}`)}</Legend>
            <Text>{t(`inclusions.typeDescription.${inclusion.type}`)}</Text>
            <div className="absolute right-0 top-0">
              <IconButton
                Icon={TrashIcon}
                tooltip={t("inclusions.delete")}
                onClick={() => {
                  deleteValue(inclusion);
                }}
              />
            </div>

            <div className="mt-3 grid grid-cols-3 gap-6 sm:grid-cols-12">
              {inclusion.type === RelocationInclusionType.Charge ? (
                <CurrencyInput
                  className="col-span-3"
                  currency={currency ?? values.currency}
                  name={`inclusions.upsert.${idx}.value`}
                  label={t("inclusions.amount")}
                />
              ) : null}
              <TextAreaInput
                className="col-span-full"
                name={`inclusions.upsert.${idx}.description`}
                label={t("inclusions.description")}
              />
            </div>
          </Fieldset>
        );
      })}

      <EmptyState excludedTypes={excludedTypes} />
    </div>
  );
}

function RelocationsInclusionsViewCard({
  relocation,
}: {
  relocation?: RelocationFieldsFragment;
}) {
  const t = useTranslations("relocation");

  if (!relocation || relocation.inclusions.length === 0) {
    return null;
  }

  return (
    <Card className="col-span-full">
      <CardHeader>
        <CardTitle>{t("inclusions.cardTitle")}</CardTitle>
        <CardDescription>{t("inclusions.cardDescription")}</CardDescription>
      </CardHeader>
      <CardContent>
        <RelocationInclusionsList relocation={relocation} />
      </CardContent>
    </Card>
  );
}

export function RelocationInclusionsList({
  relocation,
}: {
  relocation: RelocationFieldsFragment;
}) {
  const t = useTranslations("relocation");

  if (relocation.inclusions.length === 0) {
    return (
      <p className="text-sm italic text-gray-500">
        {t("inclusions.noInclusions")}
      </p>
    );
  }

  return (
    <ul className="space-y-2">
      {relocation.inclusions.map((inclusion) => {
        const Icon = inclusionIconMap[inclusion.type];

        return (
          <li
            key={inclusion.id}
            className="flex items-start gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3"
          >
            <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
              <Icon className="h-4 w-4 text-gray-600" aria-hidden="true" />
            </div>
            <div className="flex-1 space-y-1">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  {t(`inclusions.type.${inclusion.type}`)}
                </span>
                {inclusion.has_free_tank ? (
                  <span className="rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold text-green-700">
                    {t("inclusions.oneFreeTank")}
                  </span>
                ) : inclusion.value ? (
                  <span className="rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">
                    {formatCurrency(inclusion.value, relocation.currency)}
                  </span>
                ) : null}
              </div>
              {!inclusion.has_free_tank && (
                <div
                  className="text-sm leading-relaxed text-gray-700"
                  dangerouslySetInnerHTML={{
                    __html: inclusion.description ?? "--",
                  }}
                />
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const inclusionIconMap: {
  [key in RelocationInclusionType]: FC<any>;
} = {
  [RelocationInclusionType.Charge]: DollarSignIcon,
  [RelocationInclusionType.Ferry]: SailboatIcon,
  [RelocationInclusionType.Fuel]: FuelIcon,
  [RelocationInclusionType.Negative]: ExclamationTriangleIcon,
  [RelocationInclusionType.Other]: StarIcon,
};

const items = [
  {
    titleKey: "inclusions.addFerry" as const,
    descriptionKey: "inclusions.addFerryDescription" as const,
    icon: ShipIcon,
    background: "bg-sky-500",
    type: RelocationInclusionType.Ferry,
  },
  {
    titleKey: "inclusions.addOther" as const,
    descriptionKey: "inclusions.addOtherDescription" as const,
    icon: ShoppingCartIcon,
    background: "bg-pink-500",
    type: RelocationInclusionType.Other,
  },
  {
    titleKey: "inclusions.addCharge" as const,
    descriptionKey: "inclusions.addChargeDescription" as const,
    icon: DollarSignIcon,
    background: "bg-yellow-500",
    type: RelocationInclusionType.Charge,
  },
  {
    titleKey: "inclusions.addNegative" as const,
    descriptionKey: "inclusions.addNegativeDescription" as const,
    icon: ExclamationTriangleIcon,
    background: "bg-red-500",
    type: RelocationInclusionType.Negative,
  },
];

function EmptyState({
  excludedTypes = [],
}: {
  excludedTypes?: RelocationInclusionType[];
}) {
  const { values, setFieldValue } = useFormState<CreateRelocationInput>();
  const t = useTranslations("relocation");

  function hasInclusion(type: RelocationInclusionType) {
    return values.inclusions?.upsert?.some((i) => i.type === type);
  }

  const availableItems = items.filter(
    (item) => !excludedTypes.includes(item.type),
  );

  return (
    <div>
      <ul
        role="list"
        className="mt-6 grid grid-cols-1 gap-6 border-b border-t border-gray-200 py-6 sm:grid-cols-2"
      >
        {availableItems.map((item, itemIdx) => (
          <li
            key={itemIdx}
            className={cn("flow-root", {
              "pointer-events-none cursor-not-allowed opacity-50": hasInclusion(
                item.type,
              ),
            })}
          >
            <div className="relative -m-2 flex items-center space-x-4 rounded-xl p-2 focus-within:ring-2 focus-within:ring-indigo-500 hover:bg-gray-50">
              <div
                className={cn(
                  item.background,
                  "flex h-16 w-16 shrink-0 items-center justify-center rounded-lg",
                )}
              >
                <item.icon aria-hidden="true" className="h-6 w-6 text-white" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-gray-900">
                  <button
                    disabled={hasInclusion(item.type)}
                    className="focus:outline-hidden"
                    type="button"
                    onClick={() => {
                      const newIndex = values.inclusions?.upsert?.length ?? 0;
                      setFieldValue("inclusions.upsert", [
                        ...(values.inclusions?.upsert ?? []),
                        {
                          type: item.type,
                          description: "",
                          value: null!,
                        },
                      ]);

                      setTimeout(() => {
                        const fieldName =
                          item.type === RelocationInclusionType.Charge
                            ? `inclusions.upsert.${newIndex}.value`
                            : `inclusions.upsert.${newIndex}.description`;

                        const input = document.querySelector(
                          `input[name="${fieldName}"], textarea[name="${fieldName}"]`,
                        ) as HTMLInputElement | HTMLTextAreaElement;

                        input?.focus();
                      }, 100);
                    }}
                  >
                    <span aria-hidden="true" className="absolute inset-0" />
                    <span>{t(item.titleKey)}</span>
                    <span aria-hidden="true"> &rarr;</span>
                  </button>
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {t(item.descriptionKey)}
                </p>
              </div>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
